/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { onElementsAdded } from '@theme/component-loader';

/**
 * Toggles `.lazy-image--loaded` on every `<img loading="lazy">` once it has
 * actually finished loading (or failed to), so base.css's fade-in/scale
 * transition animates in step with the image actually appearing rather than
 * just the element being inserted into the DOM. Eager images are left
 * alone entirely — they're either above the fold or the confirmed LCP
 * candidate, so any animation would only delay perceived readiness.
 *
 * `load`/`error` don't bubble, but they do fire during the capture phase on
 * every ancestor — so one document-level capturing listener catches every
 * image site-wide without attaching a per-image listener.
 */
document.addEventListener(
  'load',
  (event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement && target.loading === 'lazy') {
      target.classList.add('lazy-image--loaded');
    }
  },
  { capture: true },
);

// A broken image still needs the class — otherwise it's stuck at the
// initial opacity: 0 forever, invisible even as a broken-image icon.
document.addEventListener(
  'error',
  (event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement && target.loading === 'lazy') {
      target.classList.add('lazy-image--loaded');
    }
  },
  { capture: true },
);

/** @param {Element | Document | DocumentFragment} root */
function scan(root) {
  const images = root instanceof HTMLImageElement ? [root] : root.querySelectorAll('img[loading="lazy"]');

  for (const image of images) {
    // Already resolved (e.g. served from the disk/memory cache) before
    // this script observed it — the load/error event already fired and
    // won't fire again, so catch that case here instead.
    if (image.complete) image.classList.add('lazy-image--loaded');
  }
}

scan(document);

// Soft-nav section swaps, quick-add renders, infinite scroll, etc. all
// insert markup after this initial scan — catch those too. Registered
// through @theme/component-loader's shared MutationObserver rather than
// running a separate one here, so a big batch of inserted nodes gets
// walked once per consumer via one observer callback, not once per
// independent observer instance watching the same subtree.
onElementsAdded(scan);
